
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type PrayerRow = Database['public']['Tables']['prayers']['Row'];
type PrayerInsert = Database['public']['Tables']['prayers']['Insert'];

export const PrayerService = {
  // Get all prayers
  listAll: async (currentUserId?: string) => {
    let query = supabase
      .from('prayers')
      .select('*')
      .order('created_at', { ascending: false });

    if (currentUserId) {
      const { data: blocked } = await supabase
        .from('user_blocks')
        .select('blocked_id')
        .eq('blocker_id', currentUserId);

      if (blocked?.length) {
        query = query.not('author_id', 'in', `(${blocked.map(b => b.blocked_id).join(',')})`);
      }
    }
    return query;
  },

  // Enhanced version that safely loads profiles separately
  listAllEnriched: async (currentUserId?: string) => {
    let query = supabase
      .from('prayers')
      .select('*')
      .order('created_at', { ascending: false });

    if (currentUserId) {
      const { data: blocked } = await supabase
        .from('user_blocks')
        .select('blocked_id')
        .eq('blocker_id', currentUserId);

      if (blocked?.length) {
        query = query.not('author_id', 'in', `(${blocked.map(b => b.blocked_id).join(',')})`);
      }
    }

    const { data: prayers, error } = await query;
    if (error) return { data: null, error };

    // Get unique author IDs
    const authorIds = [...new Set(prayers?.map(p => p.author_id).filter(Boolean))] as string[];
    
    if (authorIds.length === 0) {
      return { data: prayers, error: null };
    }

    // Fetch profiles separately
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, username, avatar_url')
      .in('id', authorIds);

    // Create profiles lookup
    const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);

    // Merge profiles into prayers
    const enrichedPrayers = prayers?.map(prayer => ({
      ...prayer,
      profiles: prayer.author_id ? profilesMap.get(prayer.author_id) || null : null
    }));

    return { data: enrichedPrayers, error: null };
  },

  // Get prayers by author
  listByAuthor: (authorId: string) =>
    supabase
      .from('prayers')
      .select('*')
      .eq('author_id', authorId)
      .order('created_at', { ascending: false }),

  // Get prayers by category
  listByCategory: (category: string) =>
    supabase
      .from('prayers')
      .select('*')
      .eq('category', category)
      .order('created_at', { ascending: false }),

  // Get prayers by circle
  listByCircle: (circleId: string) =>
    supabase
      .from('prayers')
      .select('*')
      .eq('circle_id', circleId)
      .order('created_at', { ascending: false }),

  // Get prayers by visibility
  listByVisibility: (visibility: string) =>
    supabase
      .from('prayers')
      .select('*')
      .eq('visibility', visibility)
      .order('created_at', { ascending: false }),

  // Get prayers by status
  listByStatus: (status: string) =>
    supabase
      .from('prayers')
      .select('*')
      .eq('status', status)
      .order('created_at', { ascending: false }),

  // Backward compatibility - simple list method
  list: () =>
    supabase
      .from('prayers')
      .select('*')
      .order('created_at', { ascending: false }),

  get: (id: string) =>
    supabase
      .from('prayers')
      .select('*')
      .eq('id', id)
      .maybeSingle(),

  add: (prayer: PrayerInsert) =>
    supabase
      .from('prayers')
      .insert([prayer])
      .select(),

  update: (id: string, updates: Partial<PrayerRow>) =>
    supabase
      .from('prayers')
      .update(updates)
      .eq('id', id),

  remove: (id: string) =>
    supabase
      .from('prayers')
      .delete()
      .eq('id', id),
};
